import React, { useEffect, useState } from "react";
import { Tabs, message } from "antd";
import { useParams } from "react-router-dom";
import { FaCamera } from "react-icons/fa";
import drEasyApi from "../../../Api/Customapi";
import { useGetUserData } from "../../../hooks/useGetUserData";
import { getImg } from "../../../Api/getImg";
import { getTokenBySlug } from "../../../hooks/useNotficationQuery";
import ChangePassword from "./ChangePassword";
import { useLanguage } from "../../../context/LanguageContext";

const { TabPane } = Tabs;

const Settings = () => {
  const { t } = useLanguage();
  const { slug } = useParams();
  const { data: user, isLoading, error, refetch } = useGetUserData(slug);
  const token = getTokenBySlug(slug);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    about: "",
    email: "",
    phoneNumber: "",
    photoId: "",
  });

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        about: user.about || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
        photoId: user.imageId || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      message.error(t('settings.editProfile.invalidImageType'));
      return;
    }

    try {
      setUploading(true);
      const formDataImg = new FormData();
      formDataImg.append("file", file);

      const { data } = await drEasyApi.post("/api/Storage/UploadImage", formDataImg, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      setFormData((prev) => ({ ...prev, photoId: data.id }));
    } catch (err) {
      console.error(err);
      message.error(t('settings.editProfile.imageUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      message.error(t('settings.editProfile.tokenMissing'));
      return;
    }

    setSubmitting(true);

    const requestBody = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      about: "TESTING-123",
      phoneNumber: formData.phoneNumber,
      imageId: formData.photoId,
      isGuest: false,
    };

    try {
      const res = await drEasyApi.post(`/api/${slug}/User/EditProfile`, requestBody, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.isSuccessStatusCode || res.status === 200) {
        message.success(t('settings.editProfile.success'));
        refetch();
      } else {
        message.error(res.data?.message || t('settings.editProfile.error'));
      }
    } catch (err) {
      console.error(err);
      message.error(t('settings.editProfile.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <div className="text-center mt-20">{t('settings.loading')}</div>;
  if (error) return <div className="text-center mt-20 text-red-500">{t('settings.loadError')}</div>;

  return (
    <div className="max-w-5xl mx-auto mt-20 bg-white p-6 rounded-xl shadow-lg mb-80">
      <Tabs tabPosition="left" defaultActiveKey="1" className="min-h-[400px]">
        {/* Edit Profile Tab */}
        <TabPane tab={t('settings.tabs.editProfile')} key="1">
          <h2 className="text-2xl text-center font-bold mb-6 text-primary">{t('settings.editProfile.title')}</h2>

          <div className="flex justify-center mb-6">
            <div className="relative">
              <img
                src={
                  getImg(formData.photoId) ||
                  "https://res.cloudinary.com/dk2uh7sho/image/upload/v1750278754/default-img_tbc2pe.jpg"
                }
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-2 border-primary"
              />
              <label className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow cursor-pointer">
                <FaCamera className="text-primary" />
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">{t('settings.editProfile.firstName')}</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('settings.editProfile.lastName')}</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('settings.editProfile.email')}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled
                className="w-full border px-3 py-2 rounded bg-gray-100"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('settings.editProfile.phoneNumber')}</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
                required
              />
            </div>

            <div className="md:col-span-2 mt-4">
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary text-white px-6 py-2 rounded disabled:opacity-50"
              >
                {submitting ? t('settings.editProfile.saving') : t('settings.editProfile.saveButton')}
              </button>
            </div>
          </form>
        </TabPane>

        {/* Change Password Tab */}
        <TabPane tab={t('settings.tabs.changePassword')} key="2">
          <ChangePassword slug={slug} token={token} />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Settings;